import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { StartPage, Button, ModalVariables, Label, SearchComponent, FilterBar } from "../../../components";
import { useNavigate } from "react-router";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import { volumeBackupService, volumeService } from "../../../api";
import { useUi } from "../../../context/ui.context";
import { useAuth } from "../../../context/user.context";
import rootConfig from "../../../config";
import { compareArrays } from "../../../utils/findIntersection";
import useUpdateEffect from "../../../hooks/useUpdateEffect";

const BACKUP_FILTER_KEYS = [
  'created_at',
  'updated_at',
  'status',
  'size',
  'region',
];
const DEFAULT_ORDERING = '-created_at';

const VolumeBackups: FC = () => {
  const {
    viewAlert,
  } = useUi()
  const { user, quotas } = useAuth()
  const [backups, setBackups] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering = '-created_at', setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [paginationData, setPaginationData] = useState<any>()
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [selectedBackup, setSelectedBackup] = useState<any>()
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [filterOptions, setFilterOptions] = useState<any>()
  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return BACKUP_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);
  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }

  const editChecked = (id: any, isAll?: boolean) => {
    if (id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item: any) => item.id === id?.id)
    if (founded) {
      if (isAll) {
        return
      } else {
        setChecked((prev: any) => {
          return prev.filter((item: any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  }

  useEffect(() => {
    if (compareArrays(backups, checked) && checked?.length !== 0) {
      setAllChecked(true)
    }
  }, [checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);

  const [t] = useTranslation()
  const navigate = useNavigate()

  const _updateBackups = () => {
    volumeBackupService.getBackupList({
      page: page || 1,
      page_size: pageSize || 10,
      ordering: ordering || DEFAULT_ORDERING,
      search: searchString || '',
      filtering: filtering || '',
    })
      .then((res) => {
        setFilterOptions(res?.data?.filter_options);
        setBackups(res.data.objects.map((backup: any) => {
          return {
            ...backup,
            navigate: backup.id,
            sizeString: `${backup.size} GB`,
            nameStr: backup.name || backup.id,
            incremental: backup.is_incremental ? t("YES") : t("NO"),
            name: (
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: '1px'
              }}>
                {backup.name || backup.id}
                {backup.description &&
                  <Label
                    title=''
                    tooltipText={backup.description}
                  />
                }
              </div>),
            created_at: timeConverterMMDDYYHHMM(backup.created_at),
          }
        }))
        setPaginationData(res.data)
        setPageLoading(false)
      })
      .catch((e) => {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
        })
        setPageLoading(false)
      })
  }

  useEffect(() => {
    _updateBackups()
    const interval = setInterval(() => {
      _updateBackups()
    }, 5000)
    return () => {
      clearInterval(interval)
    }
  }, [page,
    ordering,
    pageSize,
    searchString,
    filtering])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])

  const openModal = (modal: any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }

  const closeModal = async (values: any, setErrors?: any) => {
    let res: any;
    try {
      setLoading(true);
      switch (modalType) {
        case 'create-volume-backup':
          res = await volumeBackupService.createBackup(values);
          if (res?.data?.detail) {
            viewAlert({
              severity: 'info',
              message: res.data?.detail,
            });
          }
          _updateBackups();
          break;
        case "delete":
          try {
            if (allChecked) {
              await Promise.all(backups.map((item) => volumeService.deleteBackup(item.id)));
              _updateBackups();
              setAllChecked(false);
              setChecked([]);
            } else if (checked?.length > 0) {
              await Promise.all(checked.map((item) => volumeService.deleteBackup(item.id)));
              _updateBackups();
              setChecked([]);
            } else if (selectedBackup) {
              await volumeService.deleteBackup(selectedBackup.id);
              _updateBackups();
            }
          } catch (e: any) {
            if (e?.response?.data.detail) {
              viewAlert({
                severity: 'error',
                message: e.response.data.detail,
              });
            }
            setLoading(false);
          } finally {
            setViewModal(false);
            setActiveAnimation(false);
          }
          break;
      }
      if (modalType !== 'delete') {
        setViewModal(false)
        setActiveAnimation(false)
      }
    } catch (e: any) {
      if (e?.response?.data?.detail || e?.response?.data?.non_field_errors) {
        viewAlert({
          severity: 'error',
          message: e.response?.data?.detail || e?.response?.data?.non_field_errors,
        });
      } else if (setErrors) {
        setErrors(e.response.data)
      }
    } finally {
      if (modalType !== 'delete') {
        setLoading(false);
      }
    }
  }

  const canDelete = paginationData?.permissions?.['volumebackups.destroy'] !== false

  const items = canDelete ? [
    {
      title: t("DELETE"),
      id: '4',
      onClick: (backup: any) => {
        openModal('delete')
        setAlertTitle(t("BACKUP"))
        setAlertSubTitle([backup])
        setSelectedBackup(backup)
      },
      icon: icons.DeleteIcon,
      divider: false
    },
  ] : []

  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
  }

  const Columns = [
    {
      title: t("NAME"),
      sorted:true,
      key: 'name'
    },
    {
      title: t("CREATED_AT"),
      sorted:true,
      key: 'created_at'
    },
    {
      title: t("SIZE"),
      key: 'sizeString'
    },
    {
      title: t("INCREMENTAL"),
      key: 'incremental'
    },
    {
      title: '',
      key: 'props'
    },
  ]

  const _createFunc = () => {
    openModal('create-volume-backup')
  }

  const canCreate = paginationData?.permissions?.['volumebackups.create'] !== false

  return (
    <>
      <StartPage
        title={`${t("VOLUME_BACKUPS")}`}
        noResourceText={`${t("NO_VOLUME_BACKUPS")}`}
        createResourceButton={canCreate ? `${t('CREATE_VOLUME_BACKUP')}` : undefined}
        createResourceButtonEventOrLink={canCreate ? _createFunc : undefined}
        haveResource={backups && backups.length > 0}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              {canCreate && (
                <Button
                  title={t('CREATE_VOLUME_BACKUP')}
                  onClick={_createFunc}
                  variant='primary'
                  size='custom'
                  customStyles={{height:'40px', whiteSpace:'nowrap'}}
                />
              )}
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                {canDelete && (
                  <Button
                    onClick={() => {
                      setAlertTitle(t("VOLUME_BACKUPS"))
                      setAlertSubTitle(allChecked ? backups : checked)
                      openModal('delete')
                    }}
                    variant='icon'
                    size='display'
                    test_id='button-delete'
                    disabled={!allChecked && checked?.length === 0}
                  >
                    <img src={icons.DeleteIcon} />
                  </Button>
                )}
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        pageLoading={pageLoading}
        tableData={{
          columns: Columns,
          data: backups,
          props: items,
          allChecked: allChecked,
          setAllChecked: setAllChecked,
          checked: checked,
          setChecked: editChecked,
          paginationData: paginationData,
          updateData: _updateBackups
        }}
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
      />
    </>
  )
}

export default VolumeBackups;
